import { OTPInput as InputOTP, REGEXP_ONLY_DIGITS, type SlotProps } from 'input-otp';
import type { ReactNode } from 'react';
import { cn, PaletteUtils } from '@shared/utils';
import { ErrorMessage } from '@shared/widgets';

const DEFAULT_LENGTH = 6;

interface OTPInputProps {
  className?: string;
  error?: ReactNode;
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
}

export const OTPInput = ({ length = DEFAULT_LENGTH, ...props }: OTPInputProps) => {
  return (
    <div className={cn('flex flex-col gap-1', props.className)}>
      <InputOTP
        containerClassName='flex gap-2'
        data-slot='input-otp'
        maxLength={length}
        render={({ slots }) => slots.map((slot, idx) => <OTPSlot key={idx} {...slot} hasError={!!props.error} />)}
        pattern={REGEXP_ONLY_DIGITS}
        pasteTransformer={(pasted) => pasted.replace(/\D/g, '')}
        value={props.value}
        onChange={props.onChange}
      />
      <ErrorMessage className='text-left' text={props.error} />
    </div>
  );
};

interface OTPSlotProps extends SlotProps {
  hasError?: boolean;
}

const OTPSlot = ({ hasError, ...props }: OTPSlotProps) => {
  return (
    <div
      className={cn(
        'relative flex h-10 w-10 items-center justify-center rounded border border-solid p-1 text-center outline-none transition-colors duration-200 dark:bg-slate',
        PaletteUtils.isInsigniaPalette && [
          'border-slate-500',
          'hover:border-primary',
          hasError && !props.isActive && 'border-red',
          props.isActive && 'border-primary',
        ],
        PaletteUtils.isVcfcPalette && [
          'border-light',
          'hover:border-primary-darkest',
          hasError && !props.isActive && 'border-red',
          props.isActive && 'border-primary-darkest',
        ],
      )}
      data-active={props.isActive}
      data-slot='input-otp-slot'
      {...props}
    >
      {props.char}
      {props.hasFakeCaret && (
        <div className='pointer-events-none absolute inset-0 flex animate-caret-blink items-center justify-center'>
          <div className='h-4 w-px bg-current' />
        </div>
      )}
    </div>
  );
};
